import React, { useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Sparkles, Upload, ArrowUp, ArrowDown, Trash2, Loader2, CheckCircle2, Wand2, Image as ImageIcon } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fileToDataUrl = (f) => new Promise((res, rej) => {
  const r = new FileReader();
  r.onload = () => res(r.result);
  r.onerror = rej;
  r.readAsDataURL(f);
});

const Field = ({ label, children }) => (
  <div>
    <label className="block text-[10px] font-montserrat tracking-wider uppercase text-[#6B4E37] mb-1">{label}</label>
    {children}
  </div>
);

/**
 * AI-assisted bulk photo uploader.
 *   - Mode "single": all photos attach to ONE product (AI uses the first photo to
 *     generate copy; remaining photos become extra angles).
 *   - Mode "multi": each photo becomes a SEPARATE product with its own AI copy.
 *   - Optional per-image bg swap to brand cream via Gemini Nano Banana.
 *   - Server-side compression + thumbnail (Pillow) — fast, no extra API cost.
 */
export const AIUploaderPanel = () => {
  const [mode, setMode] = useState('single');
  const [items, setItems] = useState([]); // {id, dataUrl, ai, processed, processing, analyzing, error, bgSwap}
  const [committing, setCommitting] = useState(false);
  // Shared "single product" form: lets the admin fix price / stock once for the whole batch
  const [sharedPrice, setSharedPrice] = useState('');
  const [sharedOriginalPrice, setSharedOriginalPrice] = useState('');
  const [sharedStock, setSharedStock] = useState(1);
  const fileInputRef = useRef(null);

  const token = () => localStorage.getItem('adminToken');

  const handleFiles = async (files) => {
    const arr = Array.from(files || []).slice(0, 30);
    if (!arr.length) return;
    const newItems = await Promise.all(arr.map(async (f) => ({
      id: `it_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      dataUrl: await fileToDataUrl(f),
      ai: null,
      processed: null,
      processing: false,
      analyzing: true,
      error: null,
      bgSwap: false,
      titleDraft: '',
      descriptionDraft: '',
      altDraft: '',
      priceDraft: '',
      originalPriceDraft: '',
      stockDraft: 1,
    })));
    setItems((prev) => [...prev, ...newItems]);
    // Kick off AI analysis sequentially to avoid LLM rate spikes
    for (const it of newItems) {
      await analyzeOne(it.id, it.dataUrl);
    }
  };

  const analyzeOne = async (id, dataUrl) => {
    try {
      const r = await axios.post(`${API}/admin/ai-uploader/analyze`,
        { image: dataUrl, session_id: id },
        { headers: { Authorization: `Bearer ${token()}` }, timeout: 90000 });
      setItems((prev) => prev.map((it) => it.id === id ? {
        ...it,
        analyzing: false,
        ai: r.data,
        titleDraft: r.data.title || '',
        descriptionDraft: r.data.description || '',
        altDraft: r.data.alt_text || '',
      } : it));
    } catch (e) {
      setItems((prev) => prev.map((it) => it.id === id ? { ...it, analyzing: false, error: e.response?.data?.detail || 'AI failed' } : it));
    }
  };

  const updateItem = (id, patch) => setItems((prev) => prev.map((it) => it.id === id ? { ...it, ...patch } : it));
  const removeItem = (id) => setItems((prev) => prev.filter((it) => it.id !== id));
  const moveItem = (id, dir) => {
    setItems((prev) => {
      const idx = prev.findIndex((it) => it.id === id);
      if (idx < 0) return prev;
      const j = idx + dir;
      if (j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  };

  const processAndCommit = async () => {
    if (items.length === 0) { toast.error('Upload at least one photo'); return; }
    setCommitting(true);

    try {
      // Step 1: process every image through /process — uploads to Cloudinary
      const uploaded = [];
      for (const it of items) {
        updateItem(it.id, { processing: true });
        try {
          const r = await axios.post(`${API}/admin/ai-uploader/process`,
            { image: it.dataUrl, swap_background: !!it.bgSwap, slug: it.ai?.slug || 'product', session_id: it.id },
            { headers: { Authorization: `Bearer ${token()}` }, timeout: 120000 });
          uploaded.push({ item: it, urls: r.data });
          updateItem(it.id, { processing: false, processed: r.data });
        } catch (e) {
          updateItem(it.id, { processing: false, error: e.response?.data?.detail || 'Upload failed' });
          throw new Error(`Failed to upload "${it.titleDraft || it.id}"`);
        }
      }

      // Step 2: commit to products
      if (mode === 'single') {
        const first = uploaded[0];
        const images = uploaded.map(u => u.urls.image_url);
        const payload = {
          name: first.item.titleDraft.trim() || 'New product',
          category: first.item.ai?.category || 'Suits',
          description: first.item.descriptionDraft.trim(),
          price: parseInt(sharedPrice, 10) || 0,
          originalPrice: sharedOriginalPrice ? parseInt(sharedOriginalPrice, 10) : null,
          stock: parseInt(sharedStock, 10) || 0,
          images,
          visible: true,
        };
        await axios.post(`${API}/admin/products`, payload, { headers: { Authorization: `Bearer ${token()}` } });
        toast.success(`Created 1 product with ${images.length} photos`);
      } else {
        // multi-mode: 1 product per photo
        for (const u of uploaded) {
          const payload = {
            name: u.item.titleDraft.trim() || u.item.ai?.title || 'New product',
            category: u.item.ai?.category || 'Suits',
            description: u.item.descriptionDraft.trim(),
            price: parseInt(u.item.priceDraft, 10) || 0,
            originalPrice: u.item.originalPriceDraft ? parseInt(u.item.originalPriceDraft, 10) : null,
            stock: parseInt(u.item.stockDraft, 10) || 0,
            images: [u.urls.image_url],
            visible: true,
          };
          await axios.post(`${API}/admin/products`, payload, { headers: { Authorization: `Bearer ${token()}` } });
        }
        toast.success(`Created ${uploaded.length} products`);
      }
      setItems([]);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'Could not finish');
    } finally {
      setCommitting(false);
    }
  };

  return (
    <Card className="bg-white border-none shadow-lg p-6" data-testid="ai-uploader-panel">
      <div className="flex items-start justify-between gap-3 mb-5 flex-wrap">
        <div>
          <h2 className="text-2xl font-seasons text-[#2C2C2C] flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-[#C4B5A0]" /> AI Photo Uploader
          </h2>
          <p className="text-sm font-montserrat text-[#8B7D6B]">
            Upload from your iPhone — auto-compress, smart thumbnails, AI titles, descriptions, alt-text & SEO filenames.
          </p>
        </div>
        {/* Mode toggle */}
        <div className="inline-flex rounded-full bg-[#FAF6EE] border border-[#D4C4B0] p-1 text-xs font-montserrat" data-testid="ai-mode-toggle">
          {[
            { v: 'single', label: 'Single product · many photos' },
            { v: 'multi', label: 'Many products · one photo each' },
          ].map((m) => (
            <button
              key={m.v}
              type="button"
              onClick={() => setMode(m.v)}
              data-testid={`ai-mode-${m.v}`}
              className={`px-4 py-1.5 rounded-full tracking-wider uppercase transition ${mode === m.v ? 'bg-[#6B4E37] text-white' : 'text-[#6B4E37] hover:bg-white'}`}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>

      {/* Shared price/stock (single mode only) */}
      {mode === 'single' && (
        <div className="bg-[#FAF6EE] border border-[#D4C4B0] rounded-lg p-3 mb-4 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="ai-shared-fields">
          <Field label="Price ₹"><Input type="number" value={sharedPrice} onChange={(e) => setSharedPrice(e.target.value)} placeholder="2999" data-testid="ai-shared-price" /></Field>
          <Field label="Original Price ₹"><Input type="number" value={sharedOriginalPrice} onChange={(e) => setSharedOriginalPrice(e.target.value)} placeholder="3999" /></Field>
          <Field label="Stock"><Input type="number" min={0} value={sharedStock} onChange={(e) => setSharedStock(e.target.value)} /></Field>
        </div>
      )}

      {/* File picker */}
      <div className="border-2 border-dashed border-[#D4C4B0] rounded-lg p-6 text-center bg-[#FAF6EE]/40 mb-4">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }}
          data-testid="ai-file-input"
        />
        <Button
          onClick={() => fileInputRef.current?.click()}
          className="bg-[#6B4E37] hover:bg-[#5A4030] text-white"
          data-testid="ai-pick-files"
        >
          <Upload className="h-4 w-4 mr-2" /> Pick photos from device
        </Button>
        <p className="text-[11px] font-montserrat text-[#8B7D6B] mt-2">
          Tap "Take Photo or Photo Library" on iPhone · up to 30 at once · JPEG/PNG/HEIC
        </p>
      </div>

      {/* Item list */}
      {items.length > 0 && (
        <div className="space-y-3 mb-5" data-testid="ai-items">
          {items.map((it, idx) => (
            <div key={it.id} className="bg-white border border-[#F0EDE5] rounded-lg p-3" data-testid={`ai-item-${idx}`}>
              <div className="flex gap-3">
                <img src={it.dataUrl} alt={`upload ${idx + 1}`} className="w-24 h-32 object-cover rounded shrink-0 bg-[#F5F3F0]" />
                <div className="flex-1 min-w-0 space-y-2">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="text-[11px] font-montserrat tracking-wider uppercase text-[#8B7D6B]">
                      {it.analyzing ? (
                        <span className="inline-flex items-center gap-1.5"><Loader2 className="h-3 w-3 animate-spin" /> Analysing with AI…</span>
                      ) : it.error ? (
                        <span className="text-red-700">⚠ {it.error}</span>
                      ) : it.ai ? (
                        <span className="inline-flex items-center gap-1.5 text-green-700"><CheckCircle2 className="h-3 w-3" /> AI ready · {it.ai.category || 'Uncategorised'}</span>
                      ) : null}
                    </div>
                    <div className="flex gap-1">
                      <button type="button" onClick={() => moveItem(it.id, -1)} disabled={idx === 0} className="p-1 text-[#8B7D6B] disabled:opacity-30" aria-label="Move up" data-testid={`ai-item-up-${idx}`}>
                        <ArrowUp className="h-4 w-4" />
                      </button>
                      <button type="button" onClick={() => moveItem(it.id, 1)} disabled={idx === items.length - 1} className="p-1 text-[#8B7D6B] disabled:opacity-30" aria-label="Move down" data-testid={`ai-item-down-${idx}`}>
                        <ArrowDown className="h-4 w-4" />
                      </button>
                      <button type="button" onClick={() => removeItem(it.id)} className="p-1 text-red-600 hover:bg-red-50 rounded" aria-label="Remove" data-testid={`ai-item-remove-${idx}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  {!it.error && (
                    <>
                      <Field label="Title">
                        <Input value={it.titleDraft} onChange={(e) => updateItem(it.id, { titleDraft: e.target.value })} className="border-[#E8E2D5]" data-testid={`ai-item-title-${idx}`} />
                      </Field>
                      <Field label="Description">
                        <textarea
                          value={it.descriptionDraft}
                          onChange={(e) => updateItem(it.id, { descriptionDraft: e.target.value })}
                          rows={2}
                          className="w-full text-sm font-montserrat bg-white border border-[#E8E2D5] rounded-md p-2 text-[#2C2C2C] focus:outline-none focus:ring-2 focus:ring-[#C4B5A0]"
                          data-testid={`ai-item-desc-${idx}`}
                        />
                      </Field>
                      <Field label="Alt text (SEO)">
                        <Input value={it.altDraft} onChange={(e) => updateItem(it.id, { altDraft: e.target.value })} className="border-[#E8E2D5]" />
                      </Field>
                      {it.ai?.seo_filename && (
                        <p className="text-[11px] font-montserrat text-[#8B7D6B] flex items-center gap-1.5"><ImageIcon className="h-3 w-3" /> {it.ai.seo_filename}</p>
                      )}
                      {/* per-item price/stock in multi mode */}
                      {mode === 'multi' && (
                        <div className="grid grid-cols-3 gap-2 pt-1">
                          <Field label="Price ₹"><Input type="number" value={it.priceDraft} onChange={(e) => updateItem(it.id, { priceDraft: e.target.value })} placeholder="2999" data-testid={`ai-item-price-${idx}`} /></Field>
                          <Field label="Orig. ₹"><Input type="number" value={it.originalPriceDraft} onChange={(e) => updateItem(it.id, { originalPriceDraft: e.target.value })} placeholder="3999" /></Field>
                          <Field label="Stock"><Input type="number" min={0} value={it.stockDraft} onChange={(e) => updateItem(it.id, { stockDraft: e.target.value })} /></Field>
                        </div>
                      )}
                      <label className="flex items-center gap-2 mt-1 cursor-pointer">
                        <input type="checkbox" checked={!!it.bgSwap} onChange={(e) => updateItem(it.id, { bgSwap: e.target.checked })} className="h-4 w-4 accent-[#C4B5A0]" data-testid={`ai-item-bgswap-${idx}`} />
                        <span className="text-xs font-montserrat text-[#2C2C2C] inline-flex items-center gap-1">
                          <Wand2 className="h-3.5 w-3.5 text-[#6B4E37]" /> Replace background with brand cream (#FAF6EE)
                        </span>
                      </label>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {items.length > 0 && (
        <div className="flex items-center justify-between gap-3 flex-wrap pt-3 border-t border-[#F0EDE5]">
          <p className="text-xs font-montserrat text-[#8B7D6B]">
            {items.length} photo{items.length === 1 ? '' : 's'} ready · {mode === 'single' ? 'will become ONE product' : `will become ${items.length} products`}
          </p>
          <Button
            onClick={processAndCommit}
            disabled={committing || items.some(it => it.analyzing)}
            className="bg-[#C4B5A0] hover:bg-[#B0A090] text-white"
            data-testid="ai-commit-btn"
          >
            {committing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
            {mode === 'single' ? 'Create Product' : `Create ${items.length} Products`}
          </Button>
        </div>
      )}
    </Card>
  );
};
